import {observer} from 'mobx-react-lite';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EButton,
  ECancelButton,
  EDangerButton,
  ETableSingleAction,
  IconCell,
  SimpleTable,
  SimpleTableColumn,
} from 'webapp-lib/Components';

import {Dialog, DialogProps} from '#';
import {DateAndUser} from '#/DateAndUser';
import {UserInitialsWithTooltip} from '#/UserInitialsWithTooltip';
import {LockedDocumentInResponse} from '@lexian/api/documents-api';
import {UnknownDocument} from '_/assets/images';
import loading from '_/assets/images/refresh.svg';
import {DOCUMENT_ICONS_MAP} from '_/constants';
import {I18NextNS} from '_/i18n';
import {openDocument} from '_/utilities';

import {DialogType, FilesInUseStore, releaseDocument} from '../store';

import {
  InfoText,
  RefreshCounterMessage,
  RefreshCounterWrapper,
  WarningDescription,
} from './FilesInUse.styles';

const REFRESH_INTERVAL_IN_SECONDS = 5000;

type FilesInUseProps = {
  localStore: FilesInUseStore;
};

const UFilesInUse = ({localStore}: FilesInUseProps): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('documents');
  const {dialogType} = localStore;
  const filesInUse = useMemo(
    () => localStore.filesInUse,
    [localStore.filesInUse]
  );
  const [countdown, setCountdown] = useState<number>(0);

  const dialogTypeWithRefresh = useMemo(
    () =>
      dialogType === 'LOGGING_OUT' ||
      dialogType === 'CLOSING_APP' ||
      dialogType === 'UPDATING_APP' ||
      dialogType === 'FROM_MENU',
    [dialogType]
  );

  useEffect(() => {
    if (dialogTypeWithRefresh) {
      setCountdown(REFRESH_INTERVAL_IN_SECONDS / 1000);
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      const interval = setInterval(async () => {
        await localStore.refreshLockedDocuments();
        setCountdown(REFRESH_INTERVAL_IN_SECONDS / 1000);
      }, REFRESH_INTERVAL_IN_SECONDS);
      return () => clearInterval(interval);
    }
  }, [dialogTypeWithRefresh, localStore]);

  useEffect(() => {
    if (dialogTypeWithRefresh) {
      const intervalCountdown = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearInterval(intervalCountdown);
    }
  }, [dialogTypeWithRefresh, countdown]);

  useEffect(() => {
    // Trigger second timer when all files have been released and execute default action
    if (dialogTypeWithRefresh && !localStore.hasFilesInUse) {
      if (dialogType === 'LOGGING_OUT') {
        localStore.logout();
      } else if (dialogType === 'CLOSING_APP') {
        localStore.quitRegularly();
      }
    }
  }, [dialogTypeWithRefresh, dialogType, localStore, localStore.hasFilesInUse]);

  const refreshCounter = (
    <RefreshCounterWrapper key='counter'>
      <object
        aria-labelledby='countdown-message'
        data={loading}
        tabIndex={-1}
        type='image/svg+xml'
      ></object>
      <RefreshCounterMessage id='countdown-message'>
        {t('filesInUse.countdown', {
          seconds: countdown,
        })}
      </RefreshCounterMessage>
    </RefreshCounterWrapper>
  );

  const HEADERS: Record<DialogType, string> = {
    IN_AN_ACTION: t('filesInUse.header.InAnAction'),
    LAST_LOGIN: t('filesInUse.header.LastLogin'),
    FROM_MENU: t('filesInUse.header.FromMenu'),
    LOGGING_OUT: t('filesInUse.header.LoggingOut'),
    LOGGING_OUT_UNREACHABLE: t('filesInUse.header.LoggingOutUnreachable'),
    CLOSING_APP: t('filesInUse.header.ClosingApp'),
    CLOSING_APP_UNREACHABLE: t('filesInUse.header.ClosingAppUnreachable'),
    CLOSING_APP_UNAUTHORIZED: t('filesInUse.header.ClosingAppUnauthorized'),
    UPDATING_APP: t('filesInUse.header.UpdatingApp'),
    UPDATING_APP_UNREACHABLE: t('filesInUse.header.UpdatingAppUnreachable'),
    UPDATING_APP_UNAUTHORIZED: t('filesInUse.header.UpdatingAppUnauthorized'),
    NONE: '',
  };

  const showRefresh = dialogTypeWithRefresh && filesInUse.length > 0;

  const dialogProps: DialogProps = {
    actions: [
      ...(showRefresh ? [refreshCounter] : []),
      ...{
        IN_AN_ACTION: [
          <EButton key='close' onClick={localStore.clearAllFiles}>
            {t('common:actions.close')}
          </EButton>,
        ],
        LAST_LOGIN: [
          <EButton
            key='release-all'
            disabled={!localStore.hasFilesInUse}
            onClick={localStore.releaseAllStaleDocuments}
          >
            {t('filesInUse.actions.releaseAllStale')}
          </EButton>,
          <EButton key='close' onClick={localStore.clearAllFiles}>
            {t('common:actions.close')}
          </EButton>,
        ],
        FROM_MENU: [
          <EButton
            key='release-all'
            disabled={!localStore.hasFilesInUse}
            onClick={localStore.releaseAllStaleDocuments}
          >
            {t('filesInUse.actions.releaseAll')}
          </EButton>,
          <EButton key='close' onClick={localStore.clearAllFiles}>
            {t('common:actions.close')}
          </EButton>,
        ],
        LOGGING_OUT: [
          <ECancelButton key='cancel' onClick={localStore.clearAllFiles} />,
          <EButton
            key='logout'
            variant={localStore.hasFilesInUse ? 'danger' : undefined}
            onClick={localStore.logout}
          >
            {t('filesInUse.actions.logout')}
          </EButton>,
        ],
        LOGGING_OUT_UNREACHABLE: [
          <EButton key='try-again' onClick={localStore.tryClosingAgain}>
            {t('filesInUse.actions.try-again')}
          </EButton>,
          <EDangerButton key='quit-anyways' onClick={localStore.quitAnyway}>
            {t('filesInUse.actions.logout-anyways')}
          </EDangerButton>,
        ],
        CLOSING_APP: [
          <ECancelButton key='cancel' onClick={localStore.clearAllFiles} />,
          <EButton
            key='quit'
            variant={localStore.hasFilesInUse ? 'danger' : undefined}
            onClick={localStore.quitAnyway}
          >
            {t('common:actions.close')}
          </EButton>,
        ],
        CLOSING_APP_UNREACHABLE: [
          <EButton key='try-again' onClick={localStore.tryClosingAgain}>
            {t('filesInUse.actions.try-again')}
          </EButton>,
          <EDangerButton key='quit-anyways' onClick={localStore.quitAnyway}>
            {t('filesInUse.actions.quit-anyways')}
          </EDangerButton>,
        ],
        CLOSING_APP_UNAUTHORIZED: [
          <EButton key='back' onClick={localStore.clearAllFiles}>
            {t('common:actions.back')}
          </EButton>,
          <EDangerButton key='quit-anyways' onClick={localStore.quitAnyway}>
            {t('filesInUse.actions.quit-anyways')}
          </EDangerButton>,
        ],
        UPDATING_APP: [
          <ECancelButton key='cancel' onClick={localStore.clearAllFiles} />,
          <EButton
            key='update'
            variant={localStore.hasFilesInUse ? 'danger' : undefined}
            onClick={localStore.update}
          >
            {t('common:actions.update')}
          </EButton>,
        ],
        UPDATING_APP_UNREACHABLE: [
          <EButton key='try-again' onClick={localStore.tryUpdatingAgain}>
            {t('filesInUse.actions.try-again')}
          </EButton>,
          <EDangerButton key='update-anyways' onClick={localStore.update}>
            {t('filesInUse.actions.update-anyways')}
          </EDangerButton>,
        ],
        UPDATING_APP_UNAUTHORIZED: [
          <EButton key='back' onClick={localStore.clearAllFiles}>
            {t('common:actions.back')}
          </EButton>,
          <EDangerButton key='update-anyways' onClick={localStore.update}>
            {t('filesInUse.actions.update-anyways')}
          </EDangerButton>,
        ],
        NONE: [],
      }[dialogType],
    ],
    headerText: HEADERS[dialogType],
    isShown: dialogType !== 'NONE',
    onClose: localStore.clearAllFiles,
    size: 'medium',
  };

  const columns = useMemo(
    (): SimpleTableColumn<LockedDocumentInResponse>[] => [
      {
        name: 'documentType',
        cell: (item) => (
          <IconCell>{DOCUMENT_ICONS_MAP[item.documentType]}</IconCell>
        ),
        label: t('filesInUse.column.type'),
        mode: 'min',
      },
      {
        name: 'displayName',
        cell: (item) => item.displayName,
        label: t('filesInUse.column.title'),
        mode: 'break',
      },
      {
        name: 'casusDisplayName',
        cell: (item) => item.casusDisplayName,
        label: t('filesInUse.column.casus'),
        mode: 'break',
      },
      {
        name: 'inUseBy',
        cell: (item) => (
          <UserInitialsWithTooltip
            displayName={item.inUseBy.displayName}
            initials={item.inUseBy.initialLetters}
          />
        ),
        label: t('filesInUse.column.inUseBy'),
      },
      {
        name: 'lastModified',
        cell: (item) => (
          <DateAndUser
            date={item.lastModifiedAt}
            dateFormat='withoutTimeAndTwoDigitYear'
            user={item.lastModifiedBy}
          />
        ),
        label: t('filesInUse.column.lastModified'),
      },
      {
        name: 'created',
        cell: (item) => (
          <DateAndUser
            date={item.createdAt}
            dateFormat='withoutTimeAndTwoDigitYear'
            user={item.createdBy}
          />
        ),
        label: t('filesInUse.column.createdAt'),
      },
    ],
    [t]
  );

  const singleActions = useMemo((): ETableSingleAction[] => {
    const openListAction = {
      id: 'open',
      Icon: UnknownDocument,
      callback: (document: LockedDocumentInResponse): void =>
        openDocument({...document, id: document.documentId}),
      text: t('filesInUse.actions.open'),
    };

    const openReadOnlyListAction = {
      id: 'open-readonly',
      Icon: UnknownDocument,
      inactive: false,
      callback: (document: LockedDocumentInResponse): void =>
        openDocument({...document, id: document.documentId}, {readonly: true}),
      text: t('filesInUse.actions.openReadOnly'),
    };

    const removeReleasedDocumentFromLocalStore = (
      releasedDocument: LockedDocumentInResponse
    ) => {
      if (filesInUse.length > 0) {
        const indexOfReleasedDocument = filesInUse.indexOf(releasedDocument);
        if (indexOfReleasedDocument > -1) {
          filesInUse.splice(indexOfReleasedDocument, 1);
        }
      }
    };

    const releaseListAction = {
      id: 'release',
      Icon: UnknownDocument,
      callback: async (document: LockedDocumentInResponse): Promise<void> => {
        await releaseDocument(document);
        removeReleasedDocumentFromLocalStore(document);
      },
      text: t('filesInUse.actions.release'),
    };

    return {
      IN_AN_ACTION: [openReadOnlyListAction],
      LAST_LOGIN: [releaseListAction, openListAction],
      FROM_MENU: [releaseListAction, openListAction],
      LOGGING_OUT: [releaseListAction],
      LOGGING_OUT_UNREACHABLE: [],
      CLOSING_APP: [releaseListAction],
      CLOSING_APP_UNREACHABLE: [],
      CLOSING_APP_UNAUTHORIZED: [],
      UPDATING_APP: [],
      UPDATING_APP_UNAUTHORIZED: [],
      UPDATING_APP_UNREACHABLE: [],
      NONE: [],
    }[dialogType];
  }, [dialogType, filesInUse, t]);

  let dialogContent;
  if (filesInUse.length > 0) {
    dialogContent = (
      <>
        <WarningDescription>
          {
            {
              IN_AN_ACTION: t('filesInUse.description.InAnAction'),
              LAST_LOGIN: t('filesInUse.description.LastLogin'),
              FROM_MENU: t('filesInUse.description.FromMenu'),
              LOGGING_OUT: t('filesInUse.description.LoggingOut'),
              LOGGING_OUT_UNREACHABLE: t(
                'filesInUse.description.LoggingOutUnreachable'
              ),
              CLOSING_APP: t('filesInUse.description.ClosingApp'),
              CLOSING_APP_UNREACHABLE: t(
                'filesInUse.description.ClosingAppUnreachable'
              ),
              CLOSING_APP_UNAUTHORIZED: t(
                'filesInUse.description.ClosingAppUnauthorized'
              ),
              UPDATING_APP: t('filesInUse.description.UpdatingApp'),
              UPDATING_APP_UNREACHABLE: t(
                'filesInUse.description.UpdatingAppUnreachable'
              ),
              UPDATING_APP_UNAUTHORIZED: t(
                'filesInUse.description.UpdatingAppUnauthorized'
              ),
              NONE: '',
            }[dialogType]
          }
        </WarningDescription>
        <SimpleTable
          columns={columns}
          data={filesInUse}
          dataId={(document) => document.documentId}
          singleActions={singleActions}
        />
        {showRefresh ? (
          <InfoText>{t('filesInUse.infoDuration')}</InfoText>
        ) : null}
      </>
    );
  } else {
    dialogContent = (
      <div>
        {
          {
            IN_AN_ACTION: t('filesInUse.descriptionDefaultAction.InAnAction'),
            LAST_LOGIN: t('filesInUse.descriptionDefaultAction.LastLogin'),
            FROM_MENU: t('filesInUse.descriptionDefaultAction.FromMenu'),
            LOGGING_OUT: t('filesInUse.descriptionDefaultAction.LoggingOut'),
            LOGGING_OUT_UNREACHABLE: t(
              'filesInUse.descriptionDefaultAction.LoggingOutUnreachable'
            ),
            CLOSING_APP: t('filesInUse.descriptionDefaultAction.ClosingApp'),
            CLOSING_APP_UNREACHABLE: t(
              'filesInUse.descriptionDefaultAction.ClosingAppUnreachable'
            ),
            CLOSING_APP_UNAUTHORIZED: t(
              'filesInUse.descriptionDefaultAction.ClosingAppUnauthorized'
            ),
            UPDATING_APP: t('filesInUse.descriptionDefaultAction.UpdatingApp'),
            UPDATING_APP_UNREACHABLE: t(
              'filesInUse.descriptionDefaultAction.UpdatingAppUnreachable'
            ),
            UPDATING_APP_UNAUTHORIZED: t(
              'filesInUse.descriptionDefaultAction.UpdatingAppUnauthorized'
            ),
            NONE: '',
          }[dialogType]
        }
      </div>
    );
  }
  return <Dialog {...dialogProps}>{dialogContent}</Dialog>;
};

export default observer(UFilesInUse);
